import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * Listens for the global `pro-upsell` event (fired by the api.js 402
 * interceptor) and pops a modal with the trigger reason + a one-tap
 * "Start free trial" or "Upgrade to Pro" CTA depending on whether the
 * user has already used their trial.
 */
export default function ProUpsellModal() {
  const [data, setData] = useState(null);
  const [starting, setStarting] = useState(false);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const { refresh: refreshAuth } = useAuth() || {};

  useEffect(() => {
    const handler = (e) => {
      setData(e.detail || { code: "pro_required", message: "This feature is on Shop, Cal & Meal Pro." });
      // Refresh trial-availability when modal opens.
      api.get("/billing/me").then((r) => {
        setHasUsedTrial(!!r.data?.has_used_trial);
      }).catch(() => {});
    };
    window.addEventListener("pro-upsell", handler);
    return () => window.removeEventListener("pro-upsell", handler);
  }, []);

  if (!data) return null;

  const startTrial = async () => {
    setStarting(true);
    try {
      await api.post("/billing/start-trial");
      toast.success("🎉 24-hour Pro trial active — enjoy!");
      // Bubble up to AuthContext so the rest of the app reflects new Pro state.
      if (refreshAuth) await refreshAuth();
      setData(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trial");
    } finally {
      setStarting(false);
    }
  };

  const featureLabel = data.feature || "this Pro feature";
  const close = () => setData(null);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-4 animate-in fade-in duration-200"
      data-testid="pro-upsell-modal"
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-3xl p-6 sm:p-8 shadow-2xl animate-in slide-in-from-bottom-6 duration-300"
      >
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#3B5A3E] to-[#5C7B5F] text-white flex items-center justify-center text-2xl mb-4">
          ✨
        </div>
        <h2 className="text-2xl font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
          Unlock {featureLabel}
        </h2>
        <p className="text-sm text-[#5C6B61] mt-2" data-testid="pro-upsell-message">
          {data.message}
        </p>

        <ul className="mt-5 space-y-2 text-sm text-[#2A332C]">
          <li className="flex items-start gap-2"><span className="text-[#3B5A3E] mt-0.5">✓</span> Unlimited AI food scanning</li>
          <li className="flex items-start gap-2"><span className="text-[#3B5A3E] mt-0.5">✓</span> Unlimited Smart Swap substitutes</li>
          <li className="flex items-start gap-2"><span className="text-[#3B5A3E] mt-0.5">✓</span> Weight progress chart + projected goal date</li>
          <li className="flex items-start gap-2"><span className="text-[#3B5A3E] mt-0.5">✓</span> &ldquo;What&apos;s working?&rdquo; weekly coaching insight</li>
          <li className="flex items-start gap-2"><span className="text-[#3B5A3E] mt-0.5">✓</span> Friend meal sharing &amp; weekly recap emails</li>
          <li className="flex items-start gap-2"><span className="text-[#3B5A3E] mt-0.5">✓</span> Unlimited saved foods library</li>
        </ul>

        <div className="mt-6 space-y-2">
          {!hasUsedTrial ? (
            <button
              onClick={startTrial}
              disabled={starting}
              data-testid="pro-upsell-trial-btn"
              className="w-full py-3 rounded-full text-sm font-semibold text-white bg-[#3B5A3E] hover:bg-[#2A332C] transition disabled:opacity-40"
            >
              {starting ? "Starting trial…" : "Start 24-hour free trial — no card needed"}
            </button>
          ) : (
            <Link
              to="/app/billing"
              onClick={close}
              data-testid="pro-upsell-upgrade-btn"
              className="block w-full py-3 rounded-full text-sm font-semibold text-white bg-[#3B5A3E] hover:bg-[#2A332C] transition text-center"
            >
              Upgrade to Pro
            </Link>
          )}
          <button
            onClick={close}
            data-testid="pro-upsell-close"
            className="w-full py-2.5 rounded-full text-xs text-[#5C6B61] hover:bg-[#F3F0EA] transition"
          >
            Maybe later
          </button>
        </div>
        {!hasUsedTrial && (
          <p className="text-[10px] text-[#8B9690] mt-3 text-center">
            One-time trial per account · No credit card · Cancel anytime
          </p>
        )}
      </div>
    </div>
  );
}
